import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Legal.css';

function Terms() {
  const navigate = useNavigate();

  const handleGoBack = () => {
    // If opened in new tab (no history), close the tab
    if (window.history.length <= 1) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button className="btn-go-back" onClick={handleGoBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          Close
        </button>
        <h1>Service Agreement & Terms of Service</h1>
        <p className="last-updated">Last updated: November 2024</p>

        <section className="legal-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By booking a service with <strong>NorCal Mobile Oil Change</strong> ("Company"), the customer ("Client")
            agrees to the following Terms of Service, the Service Agreement, the Cancellation Policy,
            and the Privacy Policy. If the Client does not agree, they must not book the service.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Description of Service</h2>
          <p>NorCal Mobile Oil Change provides mobile oil change services at the Client's selected location.</p>
          <p>Each standard service includes:</p>
          <ul>
            <li>Full synthetic oil (up to 5 quarts unless stated otherwise)</li>
            <li>Standard oil filter</li>
            <li>Labor</li>
            <li>7-day workmanship guarantee</li>
          </ul>
          <p>Additional oil is billed at <strong>$10 per quart</strong> unless otherwise stated.</p>
          <p>Some vehicles require:</p>
          <ul>
            <li>Premium oil</li>
            <li>Premium filters</li>
            <li>More labor time</li>
          </ul>
          <p>The Client approves any additional charges before the service begins.</p>
        </section>

        <section className="legal-section">
          <h2>3. Client Responsibilities</h2>
          <p>By booking, the Client confirms that:</p>
          <ol>
            <li>The location chosen <strong>legally allows</strong> automotive work.</li>
            <li>There is safe access to the vehicle.</li>
            <li>The Client will provide keys or remote access if needed.</li>
            <li>The Client provides the correct vehicle year, make, model, and engine type.</li>
            <li>The vehicle is on a flat, stable surface.</li>
            <li>The vehicle is not excessively lowered or lifted (unless approved).</li>
          </ol>
          <p>
            The Client understands that <strong>if the location is unsuitable</strong>,
            the service may be cancelled or rescheduled.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Liability Waiver</h2>
          <p>Client acknowledges that:</p>
          <ul>
            <li>The Company is not responsible for <strong>pre-existing damage</strong>, including
              stripped drain plugs, worn gasket surfaces, or old/cracked oil filters.</li>
            <li>Old or poorly serviced vehicles may develop leaks unrelated to the Company's work.</li>
            <li>Oil change service does not include mechanical diagnostics. Diagnostic
              scanning (error-code reading) is offered only as a separate, optional
              service and is not part of any oil change package.</li>
            <li>The Company is not liable for issues caused by prior improper service done elsewhere.</li>
          </ul>
          <p>
            If a component is already damaged, rusted, or worn — the Company may refuse service
            for safety reasons.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Payment & Charges</h2>
          <p>By booking, the Client authorizes:</p>
          <ul>
            <li>Full payment at the end of service</li>
            <li>Additional charges for:
              <ul>
                <li>Extra oil</li>
                <li>Premium filters</li>
                <li>Special tools</li>
                <li>Hard-to-reach filters</li>
                <li>Diesel vehicles exceeding included quarts</li>
              </ul>
            </li>
          </ul>
          <p>No service will be completed without Client approval of additional charges.</p>
        </section>

        <section className="legal-section">
          <h2>6. Cancellation Policy</h2>
          <ul>
            <li><strong>Free cancellation:</strong> up to 12 hours before appointment</li>
            <li><strong>Late cancellation (&lt;3 hours):</strong> $30 fee</li>
            <li><strong>No-show:</strong> full service amount may be charged</li>
          </ul>
          <p>
            Company reserves the right to cancel a service due to weather, unsafe conditions,
            or incorrect vehicle information.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Refund Policy</h2>
          <p>A full refund or free correction is provided if:</p>
          <ul>
            <li>Workmanship error is proven</li>
            <li>Wrong oil/filter was used by mistake</li>
          </ul>
          <p><strong>Refund is NOT provided if:</strong></p>
          <ul>
            <li>Client provided incorrect vehicle make/model/engine type</li>
            <li>Service is prohibited by parking rules, HOA, or office complex policies</li>
            <li>Issues are caused by pre-existing damage or vehicle condition</li>
            <li>Client cancelled late or was a no-show</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>8. Photos & Marketing Consent</h2>
          <p>
            Client agrees that NorCal Mobile Oil Change may use <strong>non-identifiable photos</strong> of the
            vehicle or service for marketing. License plates and personal data will be hidden.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Service Guarantee</h2>
          <p>All services come with a <strong>7-day workmanship guarantee</strong> covering:</p>
          <ul>
            <li>Oil leaks caused by improper filter installation</li>
            <li>Incorrect oil type</li>
          </ul>
          <p>Guarantee does NOT cover pre-existing mechanical issues.</p>
        </section>

        <section className="legal-section">
          <h2>10. Governing Law</h2>
          <p>These Terms are governed by the laws of the <strong>State of California</strong>.</p>
        </section>

        <section className="legal-section contact-section">
          <h2>Contact Us</h2>
          <p>If you have any questions about these Terms, please contact us:</p>
          <ul>
            <li>Phone: <a
              href="tel:+19168188494"
              data-event="cta_call_click"
              data-cta-location="terms_contact"
            >(916) 818-8494</a></li>
            <li>Email: <a href="mailto:info@norcalmobileoilchange.com">info@norcalmobileoilchange.com</a></li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default Terms;
